import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import ProfessionalLayout from '../components/ProfessionalLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Megaphone, Send, Eye, AlertCircle, Mail, MessageSquare, Bell, Users, Loader2, ChevronDown, ChevronUp, FileText, Settings, Plus, Pencil, Trash2 } from 'lucide-react';
import { getFlagEmoji, resolveCountryName } from '../lib/countries';

// ─────────────────────────────────────────────────────────────────
// Theme labels & colors
// ─────────────────────────────────────────────────────────────────
const THEME_LABELS = {
  flight_delay: 'Retard de vol',
  arrival_confirmed: 'Arrivée confirmée',
  customs_in_progress: 'Dédouanement en cours',
  delivery_planned: 'Livraison planifiée',
  incident_package: 'Incident colis',
  promotion: 'Promotion',
  new_rates: 'Nouveaux tarifs',
  temporary_closure: 'Fermeture temporaire',
  general_info: 'Information générale',
};

const THEME_COLORS = {
  flight_delay: 'bg-orange-100 text-orange-700 border-orange-200',
  arrival_confirmed: 'bg-green-100 text-green-700 border-green-200',
  customs_in_progress: 'bg-blue-100 text-blue-700 border-blue-200',
  delivery_planned: 'bg-teal-100 text-teal-700 border-teal-200',
  incident_package: 'bg-red-100 text-red-700 border-red-200',
  promotion: 'bg-purple-100 text-purple-700 border-purple-200',
  new_rates: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  temporary_closure: 'bg-gray-100 text-gray-700 border-gray-200',
  general_info: 'bg-sky-100 text-sky-700 border-sky-200',
};

const THEME_GROUPS = {
  'Logistique': ['flight_delay', 'arrival_confirmed', 'customs_in_progress', 'delivery_planned', 'incident_package'],
  'Commercial': ['promotion', 'new_rates', 'temporary_closure', 'general_info'],
};

// ─────────────────────────────────────────────────────────────────
// Template Management Dialog
// ─────────────────────────────────────────────────────────────────
const TemplateManagementDialog = ({ open, onOpenChange, templates, onRefresh }) => {
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [form, setForm] = useState({ theme: 'general_info', name: '', subject: '', message: '', channels: ['email', 'whatsapp'] });
  const [error, setError] = useState('');

  const resetForm = () => {
    setForm({ theme: 'general_info', name: '', subject: '', message: '', channels: ['email', 'whatsapp'] });
    setEditingTemplate(null);
    setCreating(false);
    setError('');
  };

  const startEdit = (tpl) => {
    setForm({
      theme: tpl.theme,
      name: tpl.name,
      subject: tpl.subject,
      message: tpl.message,
      channels: tpl.channels || ['email', 'whatsapp'],
    });
    setEditingTemplate(tpl);
    setCreating(false);
    setError('');
  };

  const startCreate = () => {
    resetForm();
    setCreating(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.subject.trim() || !form.message.trim()) {
      setError('Tous les champs sont requis');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (editingTemplate) {
        await api.put(`/api/broadcast/templates/${editingTemplate.id}`, form);
      } else {
        await api.post('/api/broadcast/templates', form);
      }
      resetForm();
      onRefresh();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/api/broadcast/templates/${id}`);
      setDeleteConfirm(null);
      onRefresh();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const toggleChannel = (ch) => {
    setForm(prev => ({
      ...prev,
      channels: prev.channels.includes(ch)
        ? prev.channels.filter(c => c !== ch)
        : [...prev.channels, ch],
    }));
  };

  const showForm = creating || editingTemplate;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) resetForm(); onOpenChange(v); }}>
      <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings size={18} />
            Gestion des modèles
          </DialogTitle>
        </DialogHeader>

        {error && (
          <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
            <AlertCircle size={14} />
            {error}
          </div>
        )}

        {showForm ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Thème</Label>
              <select
                value={form.theme}
                onChange={(e) => setForm({ ...form, theme: e.target.value })}
                className="w-full border border-input rounded-md px-3 py-2 text-sm bg-background"
              >
                {Object.entries(THEME_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Nom du modèle</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ex: Retard vol — notification client"
                maxLength={200}
              />
            </div>
            <div className="space-y-2">
              <Label>Sujet</Label>
              <Input
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder="Sujet du message"
                maxLength={300}
              />
            </div>
            <div className="space-y-2">
              <Label>Message</Label>
              <textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full border border-input rounded-md px-3 py-2 text-sm resize-none h-32 focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="Corps du message. Placeholders : {shipment_name}, {flight_name}, {oce_code}, {destination}"
                maxLength={2000}
              />
              <p className="text-xs text-muted-foreground">
                Placeholders : {'{shipment_name}'}, {'{flight_name}'}, {'{oce_code}'}, {'{destination}'}
              </p>
            </div>
            <div className="space-y-2">
              <Label>Canaux par défaut</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={form.channels.includes('email')} onChange={() => toggleChannel('email')} className="rounded" />
                  <Mail size={14} /> <span className="text-sm">Email</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={form.channels.includes('whatsapp')} onChange={() => toggleChannel('whatsapp')} className="rounded" />
                  <MessageSquare size={14} /> <span className="text-sm">WhatsApp</span>
                </label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={resetForm}>Annuler</Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
                {editingTemplate ? 'Enregistrer' : 'Créer'}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex justify-end">
              <Button size="sm" onClick={startCreate}>
                <Plus size={14} className="mr-1" />
                Nouveau modèle
              </Button>
            </div>

            {templates.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">Aucun modèle</p>
            ) : (
              <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                {templates.map((tpl) => (
                  <div key={tpl.id} className="border border-border rounded-md p-3 hover:bg-muted/30 transition-colors">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-sm truncate">{tpl.name}</span>
                          <Badge variant="outline" className={`text-xs ${THEME_COLORS[tpl.theme] || ''}`}>
                            {THEME_LABELS[tpl.theme] || tpl.theme}
                          </Badge>
                          {tpl.is_default && (
                            <Badge variant="secondary" className="text-xs">Défaut</Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground truncate">{tpl.subject}</p>
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{tpl.message}</p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button variant="ghost" size="sm" onClick={() => startEdit(tpl)} className="h-7 w-7 p-0">
                          <Pencil size={14} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteConfirm(tpl.id)}
                          className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Delete confirmation */}
        {deleteConfirm && (
          <div className="border-t border-border pt-3 mt-3">
            <p className="text-sm text-amber-600 mb-2">Supprimer ce modèle ? Cette action est irréversible.</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setDeleteConfirm(null)}>Annuler</Button>
              <Button variant="destructive" size="sm" onClick={() => handleDelete(deleteConfirm)}>Supprimer</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────────
// Nouveau message
// ─────────────────────────────────────────────────────────────────
const NewBroadcastTab = ({ t }) => {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [theme, setTheme] = useState('general_info');
  const [imageFile, setImageFile] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [channels, setChannels] = useState({ email: true, whatsapp: false });
  const [target, setTarget] = useState('all');
  const [countries, setCountries] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [selectedShipmentId, setSelectedShipmentId] = useState('');
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Templates
  const [templates, setTemplates] = useState([]);
  const [showTemplatesDialog, setShowTemplatesDialog] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await api.get('/api/broadcast/templates');
      setTemplates(res.data.templates || []);
    } catch {}
  }, []);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await api.get('/api/company/settings');
        const hqs = (res.data.general?.headquarters || []).map(h => ({
          name: h.name || h.country_name || '',
          code: h.code || h.country_code || '',
        }));
        const dels = (res.data.general?.delivery_countries || []).map(d => ({
          name: d.name || d.country_name || '',
          code: d.code || d.country_code || '',
        }));
        setCountries([...hqs, ...dels]);
      } catch {}
    };
    const fetchShipments = async () => {
      try {
        const res = await api.get('/api/shipments');
        setShipments(res.data || []);
      } catch {}
    };
    fetchSettings();
    fetchShipments();
    fetchTemplates();
  }, [fetchTemplates]);

  const handleSelectTemplate = (templateId) => {
    if (!templateId) return;
    const tpl = templates.find(t => t.id === templateId);
    if (!tpl) return;
    setSubject(tpl.subject);
    setMessage(tpl.message);
    setTheme(tpl.theme || 'general_info');
    const newChannels = { email: false, whatsapp: false };
    (tpl.channels || []).forEach(ch => { if (ch in newChannels) newChannels[ch] = true; });
    setChannels(newChannels);
  };

  const MARKETING_THEMES = ['promotion', 'new_rates'];
  const whatsappDisallowed = MARKETING_THEMES.includes(theme);

  const handlePreview = async () => {
    setError('');
    try {
      const params = new URLSearchParams({ target });
      if (target === 'by_country' && selectedCountries.length > 0) {
        params.set('countries', selectedCountries.join(','));
      }
      if (target === 'by_shipment' && selectedShipmentId) {
        params.set('shipment_id', selectedShipmentId);
      }
      const res = await api.get(`/api/broadcast/preview?${params}`);
      setPreview(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || t('errorPreview'));
    }
  };

  const handleImageSelect = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImageFile(f);
    setUploadingImage(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await api.post('/api/broadcast/upload-image', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImageUrl(res.data.image_url || '');
    } catch (err) {
      setError(err.response?.data?.detail || "Échec du téléversement de l'image");
      setImageFile(null);
      setImageUrl('');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSend = async () => {
    setSending(true);
    setError('');
    setSuccess('');
    try {
      const channelList = [];
      if (channels.email) channelList.push('email');
      if (channels.whatsapp && !whatsappDisallowed) channelList.push('whatsapp');

      const payload = {
        subject,
        message,
        theme,
        image_url: imageUrl || null,
        channels: channelList,
        recipient_filter: {
          target,
          countries: target === 'by_country' ? selectedCountries : [],
          shipment_id: target === 'by_shipment' ? selectedShipmentId : null,
        },
      };
      const res = await api.post('/api/broadcast/send', payload);
      const launched = t('broadcastLaunched').replace('{count}', res.data.total_recipients);
      setSuccess(res.data.whatsapp_blocked ? `${launched} — WhatsApp retiré (thème marketing ou plan non Entreprise).` : launched);
      setSubject('');
      setMessage('');
      setTheme('general_info');
      setImageFile(null);
      setImageUrl('');
      setPreview(null);
      setShowConfirm(false);
    } catch (err) {
      setError(err.response?.data?.detail || t('errorSending'));
      setShowConfirm(false);
    } finally {
      setSending(false);
    }
  };

  // Group templates by theme group for the select
  const groupedTemplates = {};
  for (const [groupName, themes] of Object.entries(THEME_GROUPS)) {
    const groupTpls = templates.filter(t => themes.includes(t.theme));
    if (groupTpls.length > 0) groupedTemplates[groupName] = groupTpls;
  }

  const selectedChannels = [];
  if (channels.email) selectedChannels.push('email');
  if (channels.whatsapp) selectedChannels.push('whatsapp');
  const canSend = subject.trim() && message.trim() && selectedChannels.length > 0
    && (target !== 'by_shipment' || selectedShipmentId);

  return (
    <div className="space-y-6">
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          <AlertCircle size={16} />
          {error}
        </div>
      )}
      {success && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-md text-green-700 text-sm">
          {success}
        </div>
      )}

      {/* Template selector */}
      <Card className="border-border">
        <CardContent className="pt-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 flex-1">
              <FileText size={16} className="text-muted-foreground flex-shrink-0" />
              <Label className="flex-shrink-0">Utiliser un modèle</Label>
              <select
                onChange={(e) => handleSelectTemplate(e.target.value)}
                defaultValue=""
                className="flex-1 border border-input rounded-md px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">-- Sélectionner un modèle --</option>
                {Object.entries(groupedTemplates).map(([group, tpls]) => (
                  <optgroup key={group} label={group}>
                    {tpls.map(tpl => (
                      <option key={tpl.id} value={tpl.id}>
                        {tpl.name} — {tpl.subject}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
            <Button variant="outline" size="sm" onClick={() => setShowTemplatesDialog(true)}>
              <Settings size={14} className="mr-1" />
              Gérer
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border">
        <CardHeader>
          <CardTitle className="text-base">{t('composeMessage')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>{t('subject')}</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={t('messageSubjectPlaceholder')}
            />
          </div>
          <div className="space-y-2">
            <Label>{t('message')}</Label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full border border-input rounded-md px-3 py-2 text-sm resize-none h-32 focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder={t('messageContentPlaceholder')}
            />
          </div>

          {/* Image optionnelle (WhatsApp — plan Entreprise) */}
          <div className="space-y-2">
            <Label>Image (optionnelle)</Label>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              disabled={uploadingImage}
              onChange={handleImageSelect}
              className="w-full text-sm"
            />
            {uploadingImage && <p className="text-xs text-muted-foreground">Téléversement…</p>}
            {imageUrl && !uploadingImage && (
              <div className="flex items-center gap-2">
                <img src={imageUrl} alt="aperçu" className="h-14 w-14 object-cover rounded border border-border" />
                <button type="button" onClick={() => { setImageUrl(''); setImageFile(null); }} className="text-xs text-red-600 hover:underline">Retirer</button>
              </div>
            )}
            <p className="text-xs text-muted-foreground">Jointe uniquement à la diffusion WhatsApp (plan Entreprise).</p>
          </div>

          {/* Canaux */}
          <div className="space-y-2">
            <Label>{t('sendingChannels')}</Label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={channels.email}
                  onChange={(e) => setChannels({ ...channels, email: e.target.checked })}
                  className="rounded"
                />
                <Mail size={16} />
                <span className="text-sm">Email</span>
              </label>
              <label className={`flex items-center gap-2 ${whatsappDisallowed ? 'opacity-50 cursor-not-allowed' : ''}`}>
                <input
                  type="checkbox"
                  checked={channels.whatsapp && !whatsappDisallowed}
                  disabled={whatsappDisallowed}
                  onChange={(e) => setChannels({ ...channels, whatsapp: e.target.checked })}
                  className="rounded"
                />
                <MessageSquare size={16} />
                <span className="text-sm">WhatsApp</span>
              </label>
            </div>
            {whatsappDisallowed && (
              <p className="text-xs text-muted-foreground">
                WhatsApp indisponible pour les thèmes promotionnels (promotion, nouveaux tarifs).
                Utilisez l'email ou la notification in-app.
              </p>
            )}
          </div>

          {/* Filtre destinataires */}
          <div className="space-y-2">
            <Label>{t('recipients')}</Label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="target"
                  checked={target === 'all'}
                  onChange={() => setTarget('all')}
                />
                <span className="text-sm">{t('all')}</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="target"
                  checked={target === 'by_country'}
                  onChange={() => setTarget('by_country')}
                />
                <span className="text-sm">{t('byCountry')}</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="target"
                  checked={target === 'by_clients'}
                  onChange={() => setTarget('by_clients')}
                />
                <span className="text-sm">{t('clients')}</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="target"
                  checked={target === 'by_shipment'}
                  onChange={() => setTarget('by_shipment')}
                />
                <span className="text-sm">{t('byShipment')}</span>
              </label>
            </div>

            {target === 'by_shipment' && (
              <div className="mt-2">
                <select
                  value={selectedShipmentId}
                  onChange={(e) => setSelectedShipmentId(e.target.value)}
                  className="w-full border border-input rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">-- {t('selectShipment')} --</option>
                  {shipments.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name || s.flight_number || s.id} — {getFlagEmoji(s.origin_country)} {resolveCountryName(s.origin_country)} → {getFlagEmoji(s.destination_country)} {resolveCountryName(s.destination_country)}
                      {s.status ? ` (${s.status})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {target === 'by_country' && (
              <div className="mt-2 flex flex-wrap gap-2">
                {countries.map((c, i) => (
                  <label key={i} className="flex items-center gap-1 text-sm bg-muted rounded px-2 py-1">
                    <input
                      type="checkbox"
                      checked={selectedCountries.includes(c.code)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedCountries([...selectedCountries, c.code]);
                        } else {
                          setSelectedCountries(selectedCountries.filter(x => x !== c.code));
                        }
                      }}
                      className="rounded"
                    />
                    {c.name} ({c.code})
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={handlePreview} disabled={!canSend}>
              <Eye size={14} className="mr-1" />
              {t('preview')}
            </Button>
            <Button onClick={() => setShowConfirm(true)} disabled={!canSend}>
              <Send size={14} className="mr-1" />
              {t('send')}
            </Button>
          </div>

          {/* Preview */}
          {preview && (
            <div className="bg-muted border border-border rounded-md p-4 text-sm">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-3">
                <p className="font-medium">{t('recipientsPreview')} :</p>
                <span><span className="text-muted-foreground">{t('total')} :</span> <strong>{preview.total}</strong></span>
                <span><span className="text-muted-foreground">{t('email')} :</span> <strong>{preview.email}</strong></span>
                <span><span className="text-muted-foreground">WhatsApp :</span> <strong>{preview.whatsapp}</strong></span>
              </div>

              {/* Liste réelle des destinataires */}
              {preview.contacts?.length > 0 ? (
                <div className="max-h-64 overflow-y-auto border border-border rounded-md divide-y divide-border bg-background">
                  {preview.contacts.map((c, i) => (
                    <div key={i} className="flex items-center justify-between gap-3 px-3 py-2">
                      <div className="min-w-0">
                        <div className="font-medium truncate">{c.name || '—'}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {[c.email, c.phone].filter(Boolean).join(' · ') || '—'}
                        </div>
                      </div>
                      {c.role && (
                        <Badge variant="outline" className="text-[10px] shrink-0">{c.role}</Badge>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-xs">Aucun contact.</p>
              )}
              {preview.contacts_truncated && (
                <p className="text-xs text-muted-foreground mt-2">
                  Liste tronquée aux 300 premiers ({preview.total} destinataires au total).
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('confirmSending')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p><strong>{t('subject')} :</strong> {subject}</p>
            <p><strong>{t('channels')} :</strong> {selectedChannels.join(', ')}</p>
            <p><strong>{t('target')} :</strong> {
              target === 'all' ? t('allContacts') :
              target === 'by_clients' ? t('clients') :
              target === 'by_shipment' ? `${t('shipment')} : ${shipments.find(s => s.id === selectedShipmentId)?.name || selectedShipmentId}` :
              `${t('country')} : ${selectedCountries.join(', ')}`
            }</p>
            <p className="text-amber-600 text-xs">
              {t('irreversibleAction')}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirm(false)}>{t('cancel')}</Button>
            <Button onClick={handleSend} disabled={sending}>
              {sending ? t('sending') : t('confirmSending')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Management Dialog */}
      <TemplateManagementDialog
        open={showTemplatesDialog}
        onOpenChange={setShowTemplatesDialog}
        templates={templates}
        onRefresh={fetchTemplates}
      />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────
// Historique
// ─────────────────────────────────────────────────────────────────
const HistoryTab = ({ t }) => {
  const { language } = useLanguage();
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [expandedId, setExpandedId] = useState(null);
  const [recipientsData, setRecipientsData] = useState({});
  const [loadingRecipients, setLoadingRecipients] = useState(null);
  const limit = 20;

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/broadcast/history?skip=${page * limit}&limit=${limit}`);
      setBroadcasts(res.data.broadcasts || []);
      setTotal(res.data.total || 0);
    } catch {}
    setLoading(false);
  }, [page]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const toggleRecipients = async (broadcastId) => {
    if (expandedId === broadcastId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(broadcastId);
    if (!recipientsData[broadcastId]) {
      setLoadingRecipients(broadcastId);
      try {
        const res = await api.get(`/api/broadcast/${broadcastId}/recipients`);
        setRecipientsData(prev => ({ ...prev, [broadcastId]: res.data }));
      } catch {
        setRecipientsData(prev => ({ ...prev, [broadcastId]: { recipients: [], status: 'error' } }));
      } finally {
        setLoadingRecipients(null);
      }
    }
  };

  const statusBadge = (status) => {
    const map = {
      sending: 'bg-blue-100 text-blue-700 border-blue-200',
      sent: 'bg-green-100 text-green-700 border-green-200',
      partial_failure: 'bg-amber-100 text-amber-700 border-amber-200',
      failed: 'bg-red-100 text-red-700 border-red-200',
    };
    const labels = {
      sending: t('inProgress'),
      sent: t('sent'),
      partial_failure: t('partial'),
      failed: t('failed'),
    };
    return (
      <Badge variant="outline" className={map[status] || ''}>
        {labels[status] || status}
      </Badge>
    );
  };

  const channelIcon = (channel) => {
    if (channel === 'email') return <Mail size={14} className="text-gray-400" />;
    if (channel === 'whatsapp') return <MessageSquare size={14} className="text-gray-400" />;
    return <Bell size={14} className="text-gray-400" />;
  };

  if (loading) {
    return <div className="text-center py-8 text-muted-foreground">{t('loading')}</div>;
  }

  return (
    <div className="space-y-4">
      {broadcasts.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">{t('noBroadcastsSent')}</div>
      ) : (
        <>
          <div className="rounded-md border border-border overflow-x-auto">
            <table className="w-full text-sm min-w-[600px]">
              <thead className="bg-muted border-b border-border">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">{t('date')}</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">{t('subject')}</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">{t('channels')}</th>
                  <th className="text-right px-4 py-3 font-medium text-muted-foreground">{t('recipients')}</th>
                  <th className="text-right px-4 py-3 font-medium text-muted-foreground">{t('sent')}</th>
                  <th className="text-center px-4 py-3 font-medium text-muted-foreground">{t('status')}</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {broadcasts.map((b) => (
                  <React.Fragment key={b.id}>
                    <tr className="hover:bg-muted">
                      <td className="px-4 py-3 text-muted-foreground text-xs">
                        {b.created_at ? new Date(b.created_at).toLocaleString('fr-FR') : '—'}
                      </td>
                      <td className="px-4 py-3 font-medium text-foreground">{b.subject}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          {(b.channels || []).map(ch => (
                            <Badge key={ch} variant="outline" className="text-xs">
                              {ch === 'email' ? 'Email' : ch === 'whatsapp' ? 'WhatsApp' : 'In-App'}
                            </Badge>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right text-foreground">{b.total_recipients}</td>
                      <td className="px-4 py-3 text-right text-foreground">
                        {(b.sent_email || 0) + (b.sent_whatsapp || 0) + (b.sent_in_app || 0)}
                      </td>
                      <td className="px-4 py-3 text-center">{statusBadge(b.status)}</td>
                      <td className="px-4 py-3 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleRecipients(b.id)}
                          className="gap-1 text-xs"
                        >
                          <Users size={14} />
                          {expandedId === b.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </Button>
                      </td>
                    </tr>
                    {expandedId === b.id && (
                      <tr>
                        <td colSpan={7} className="px-4 py-3 bg-muted/50">
                          {loadingRecipients === b.id ? (
                            <div className="flex items-center justify-center gap-2 py-4 text-sm text-muted-foreground">
                              <Loader2 size={16} className="animate-spin" />
                              {t('loadingRecipients')}
                            </div>
                          ) : recipientsData[b.id]?.status === 'sending' ? (
                            <div className="flex items-center justify-center gap-2 py-4 text-sm text-muted-foreground">
                              <Loader2 size={16} className="animate-spin" />
                              {t('sendingInProgress')}
                            </div>
                          ) : recipientsData[b.id]?.recipients?.length > 0 ? (
                            <div className="max-h-72 overflow-y-auto rounded border border-border bg-background">
                              <table className="w-full text-sm">
                                <thead className="bg-muted/60 sticky top-0">
                                  <tr className="text-left text-xs text-muted-foreground">
                                    <th className="px-3 py-2 font-medium">#</th>
                                    <th className="px-3 py-2 font-medium">{language === 'en' ? 'Name' : 'Nom'}</th>
                                    <th className="px-3 py-2 font-medium">{language === 'en' ? 'Contact' : 'Numéro / Email'}</th>
                                    <th className="px-3 py-2 font-medium text-center">{language === 'en' ? 'Channel' : 'Canal'}</th>
                                    <th className="px-3 py-2 font-medium text-center">{language === 'en' ? 'Status' : 'Statut'}</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                  {recipientsData[b.id].recipients.map((r, i) => (
                                    <tr key={i} className="hover:bg-muted/40">
                                      <td className="px-3 py-2 text-muted-foreground text-xs">{i + 1}</td>
                                      <td className="px-3 py-2 font-medium text-foreground">{r.name || '—'}</td>
                                      <td className="px-3 py-2 text-muted-foreground font-mono text-xs break-all">{r.contact || '—'}</td>
                                      <td className="px-3 py-2">
                                        <div className="flex items-center justify-center gap-1">
                                          {channelIcon(r.channel)}
                                          <span className="text-xs capitalize">{r.channel === 'whatsapp' ? 'WhatsApp' : r.channel === 'email' ? 'Email' : 'In-App'}</span>
                                        </div>
                                      </td>
                                      <td className="px-3 py-2 text-center">
                                        <Badge variant={r.status === 'sent' ? 'default' : 'destructive'} className="text-xs">
                                          {r.status === 'sent' ? t('sent') : t('failed')}
                                        </Badge>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground py-2 text-center">
                              {t('noRecipientsRegistered')}
                            </p>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {total > limit && (
            <div className="flex justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage(p => p - 1)}
              >
                {t('previous')}
              </Button>
              <span className="text-sm text-muted-foreground py-1">
                Page {page + 1} / {Math.ceil(total / limit)}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={(page + 1) * limit >= total}
                onClick={() => setPage(p => p + 1)}
              >
                {t('next')}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────
// Main: BroadcastPage
// ─────────────────────────────────────────────────────────────────
const BroadcastPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();

  useEffect(() => {
    if (user && !['company_admin', 'super_admin'].includes(user.role)) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  if (!user) return null;
  if (!['company_admin', 'super_admin'].includes(user.role)) return null;

  return (
    <ProfessionalLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Megaphone size={24} className="text-muted-foreground" />
          <div>
            <h1 className="text-2xl font-bold">{t('broadcast')}</h1>
            <p className="text-muted-foreground text-sm">{t('broadcastDesc')}</p>
          </div>
        </div>

        <Tabs defaultValue="compose">
          <TabsList className="mb-4">
            <TabsTrigger value="compose">{t('newMessage')}</TabsTrigger>
            <TabsTrigger value="history">{t('history')}</TabsTrigger>
          </TabsList>

          <TabsContent value="compose">
            <NewBroadcastTab t={t} />
          </TabsContent>

          <TabsContent value="history">
            <HistoryTab t={t} />
          </TabsContent>
        </Tabs>
      </div>
    </ProfessionalLayout>
  );
};

export default BroadcastPage;
